import { BaseService, ServiceError, ValidationSchema } from './BaseService';
import { 
  objectionResponseRepository, 
  ObjectionResponse 
} from '../repositories/ObjectionResponseRepository';

/**
 * Input type for creating an objection response
 */
export interface CreateObjectionResponseInput {
  company_id: string;
  objection_text: string;
  response_template: string;
  category: ObjectionResponse['category'];
  effectiveness_score?: number;
}

/**
 * Input type for updating an objection response
 */
export interface UpdateObjectionResponseInput {
  objection_text?: string;
  response_template?: string;
  category?: ObjectionResponse['category'];
  effectiveness_score?: number;
}

/**
 * Service for managing objection responses
 * Handles business logic and validation for objection handling library
 */
export class ObjectionResponseService extends BaseService {
  /**
   * Validation schema for creating an objection response
   */
  private createSchema: ValidationSchema = {
    company_id: {
      required: true,
      type: 'string',
      message: 'Company ID is required'
    },
    objection_text: {
      required: true,
      type: 'string',
      min: 5,
      max: 500,
      message: 'Objection text must be between 5 and 500 characters'
    },
    response_template: {
      required: true,
      type: 'string',
      min: 10,
      message: 'Response template must be at least 10 characters'
    },
    category: {
      required: true,
      type: 'string',
      custom: (value) => {
        const validCategories = ['price', 'hesitation', 'competition', 'coverage', 'timing', 'trust', 'other'];
        return validCategories.includes(value) || 'Invalid category';
      }
    },
    effectiveness_score: {
      required: false,
      type: 'number',
      min: 0,
      max: 5,
      message: 'Effectiveness score must be between 0 and 5'
    }
  };

  /**
   * Get all objection responses for a company
   */
  async getResponses(companyId: string): Promise<ObjectionResponse[]> {
    try {
      return await objectionResponseRepository.findByCompany(companyId);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get objection responses by category
   */
  async getResponsesByCategory(
    companyId: string,
    category: ObjectionResponse['category']
  ): Promise<ObjectionResponse[]> {
    try {
      return await objectionResponseRepository.findByCategory(companyId, category);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get top performing objection responses
   */
  async getTopPerforming(companyId: string, limit: number = 10): Promise<ObjectionResponse[]> {
    try {
      return await objectionResponseRepository.getTopPerforming(companyId, limit);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get most frequently used objections
   */
  async getMostUsed(companyId: string, limit: number = 10): Promise<ObjectionResponse[]> {
    try {
      return await objectionResponseRepository.getMostUsed(companyId, limit);
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Get a single objection response by ID
   */
  async getResponse(id: string): Promise<ObjectionResponse> {
    try {
      const response = await objectionResponseRepository.findById(id);
      if (!response) {
        throw ServiceError.notFound('Objection response', id);
      }
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Create a new objection response
   */
  async createResponse(input: CreateObjectionResponseInput): Promise<ObjectionResponse> {
    try {
      // Validate input
      this.validate(input, this.createSchema);

      // Sanitize data
      const sanitized = {
        company_id: input.company_id,
        objection_text: this.sanitizeString(input.objection_text) || '',
        response_template: this.sanitizeString(input.response_template) || '',
        category: input.category,
        effectiveness_score: input.effectiveness_score !== undefined ? input.effectiveness_score : 3.0,
        usage_count: 0
      };

      // Create response
      const response = await objectionResponseRepository.create(sanitized);

      // Emit event
      await this.emit('objection.created', { response });

      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update an existing objection response
   */
  async updateResponse(id: string, input: UpdateObjectionResponseInput): Promise<ObjectionResponse> {
    try {
      // Check if response exists
      const existing = await objectionResponseRepository.findById(id);
      if (!existing) {
        throw ServiceError.notFound('Objection response', id);
      }

      // Sanitize data
      const sanitized: Partial<ObjectionResponse> = {};
      if (input.objection_text) {
        sanitized.objection_text = this.sanitizeString(input.objection_text) || undefined;
      }
      if (input.response_template) {
        sanitized.response_template = this.sanitizeString(input.response_template) || undefined;
      }
      if (input.category) sanitized.category = input.category;
      if (input.effectiveness_score !== undefined) {
        if (input.effectiveness_score < 0 || input.effectiveness_score > 5) {
          throw ServiceError.validation([{
            field: 'effectiveness_score',
            message: 'Effectiveness score must be between 0 and 5'
          }]);
        }
        sanitized.effectiveness_score = input.effectiveness_score;
      }

      // Update response
      const updated = await objectionResponseRepository.update(id, sanitized);

      // Emit event
      await this.emit('objection.updated', { response: updated });

      return updated;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Increment usage count when an objection response is used
   */
  async recordUsage(id: string): Promise<ObjectionResponse> {
    try {
      const response = await objectionResponseRepository.incrementUsage(id);
      await this.emit('objection.used', { response });
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Update effectiveness score
   */
  async updateEffectiveness(id: string, score: number): Promise<ObjectionResponse> {
    try {
      if (score < 0 || score > 5) {
        throw ServiceError.validation([{
          field: 'score',
          message: 'Effectiveness score must be between 0 and 5'
        }]);
      }

      const response = await objectionResponseRepository.updateEffectiveness(id, score);
      await this.emit('objection.effectiveness.updated', { response });
      return response;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * Delete an objection response
   */
  async deleteResponse(id: string): Promise<void> {
    try {
      // Check if response exists
      const response = await objectionResponseRepository.findById(id);
      if (!response) {
        throw ServiceError.notFound('Objection response', id);
      }

      // Delete response
      await objectionResponseRepository.delete(id);

      // Emit event
      await this.emit('objection.deleted', { responseId: id });
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const objectionResponseService = new ObjectionResponseService();
